// Forma para llenar cotizacion rapida

import React, { useRef, useState, useEffect } from 'react';
import { Card, CardContent, Grid, TextField, Button, Box, Typography, CircularProgress, Alert, MenuItem } from '@mui/material';
import { getServiceTypes } from './ServiceType';
import {
  FIELD_LABELS,
  zipCodeValidator,
  emailValidator,
  nameValidator,
  phoneValidator,
  requiredValidator,
  validateField
} from '../validators';
import { apiFetchJson } from '../fetchWrapper';

const QuickQuotationForm = () => {
  const [form, setForm] = useState({
    firstname: '',
    lastnameFirst: '',
    lastnameSecond: '',
    email: '',
    zipCode: '',
    elementsNumber: '0',
    phoneNumber: '',
    organizationName: '',
    descripcion: '',
    serviceType: '',
  });

  const phoneRegex = /^\+?[0-9]{7,15}$/;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    if (window.turnstile && captchaRef.current) {
      window.turnstile.render(captchaRef.current, {
        sitekey: "0x4AAAAAAFCG65tETcJ8IvqN",
        callback: (token) => setTurnstileToken(token),
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate phone
    if (form.phoneNumber && !phoneRegex.test(form.phoneNumber)) {
      setError('El número de teléfono no es válido. Debe tener 10 a 13 dígitos y opcionalmente comenzar con +');
      setLoading(false);
      return;
    }

    const payload = {
      descripcion: form.descripcion.trim(),
      contactPerson: {
        firstname: form.firstname.trim(),
        lastnameFirst: form.lastnameFirst.trim(),
        lastnameSecond: form.lastnameSecond.trim(),
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
      },
      zipCode: form.zipCode.trim(),
      organizationName: form.organizationName.trim(),
      elementsNumber: form.elementsNumber,
      serviceType: form.serviceType.trim(),
      status: "NEW",
    };

    try {
      const data = await apiFetchJson('/api/quotations', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!data) throw new Error('No response from server');

      setSuccess(true);
      setForm({
        firstname: '',
        lastnameFirst: '',
        lastnameSecond: '',
        email: '',
        phoneNumber: '',
        serviceType: '',
        elementsNumber: '0',
        organizationName: '',
        zipCode: '',
        descripcion: '',
      });
    } catch (err) {
      console.error('Failed to submit quotation', err);
      setError(err.message || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors(prev => {
      const next = { ...prev };
      let error = null;

      if (name === 'email' || name === 'contactPerson.email') {
        error = validateField(value, [
          requiredValidator,
          emailValidator
        ]);
      }

      if (name === 'phoneNumber' || name === 'mobileNumber') {
        error = validateField(value, [
          requiredValidator,
          phoneValidator
        ]);
      }

      if (name === 'firstname' || name === 'lastnameFirst' || name === 'lastnameSecond' || name === 'organizationName') {
        error = validateField(value, [
          requiredValidator,
          (v) => nameValidator(v, FIELD_LABELS[name])
        ]);
      }

      if (name === 'zipCode') {
        error = validateField(value, [
          requiredValidator,
          zipCodeValidator
        ]);
      }

      if (error) {
        next[name] = error;
      } else {
        delete next[name];
      }

      return next;
    });
  };

  return (

    <Card sx={{
      maxWidth: 900,
      mx: 'auto',
      width: '100%',
      p: 1,
      backgroundColor: '#f9f9f9',
    }}
    >
      <CardContent sx={{ pt: 2 }}>

        <Typography variant="h5"
          sx={{
            color: '#0A2647',
            fontSize: '16px',
            display: 'flex',
          }}
        >
          Llene el formulario para recibir una cotización rápida
        </Typography>

        {error &&
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {success &&
          <Alert severity="success" sx={{ mb: 2 }}>
            Cotización enviada correctamente. En breve te haremos llegar la cotización a los datos
            de contacto proporcionados.
          </Alert>}

        <form onSubmit={handleSubmit}>

          <Box sx={{ mt: 1, mb: 2 }}>

            <Grid container spacing={2}>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Nombre"
                  error={!!errors.firstname}
                  helperText={errors.firstname}
                  name="firstname"
                  value={form.firstname}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Primer Apellido"
                  error={!!errors.lastnameFirst}
                  helperText={errors.lastnameFirst}
                  name="lastnameFirst"
                  value={form.lastnameFirst}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Segundo Apellido"
                  error={!!errors.lastnameSecond}
                  helperText={errors.lastnameSecond}
                  name="lastnameSecond"
                  value={form.lastnameSecond}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  sx={{ flex: '1 1 30%' }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Nombre de la Organización"
                  error={!!errors.organizationName}
                  helperText={errors.organizationName}
                  name="organizationName"
                  value={form.organizationName}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                  sx={{ flex: '1 1 30%' }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  name="email"
                  error={!!errors.email}
                  helperText={errors.email}
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="phoneNumber"
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  value={form.phoneNumber}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 13 } }}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Codigo Postal"
                  name="zipCode"
                  error={!!errors.zipCode}
                  helperText={errors.zipCode}
                  value={form.zipCode}
                  inputMode="numeric"
                  slotProps={{ htmlInput: { maxLength: 5 } }}
                  onChange={handleChange}
                  required
                >
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select label="Tipo de servicio"
                  name="serviceType"
                  value={form.serviceType}
                  onChange={handleChange}
                  required
                >
                  {
                    getServiceTypes().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  }
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Numero de elementos"
                  name="elementsNumber"
                  type='number'
                  value={form.elementsNumber}
                  onChange={(e) => {
                    const raw = e.target.value;

                    // Allow empty input (important for Firefox)
                    if (raw === '') {
                      setForm((prev) => ({
                        ...prev,
                        elementsNumber: '',
                      }));
                      return;
                    }

                    let value = Number(raw);

                    if (value < 1) value = 1;
                    if (value > 100) value = 100;

                    setForm((prev) => ({
                      ...prev,
                      elementsNumber: value,
                    }));
                  }}
                >
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Descripción"
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  slotProps={{ htmlInput: { maxLength: 512 } }}
                  multiline rows={4}
                />
              </Grid>
            </Grid>
          </Box>

          <Typography variant="h5" gutterBottom
            sx={{
              color: '#989898',
              fontSize: '14px',
              display: 'flex',
            }}
          >
            * Campos requeridos
          </Typography>

          <Box mt={2} textAlign="right">
            <div ref={captchaRef} />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: '#0A2647',
                color: 'white',
                fontSize: '80%',
                fontWeight: 'demibold',
                width: 200,
                height: 40,
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Cotizar'}
            </Button>

          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default QuickQuotationForm;
